import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { FaTimes, FaMobileAlt, FaCheckCircle, FaExclamationCircle, FaCar, FaIdCard } from 'react-icons/fa';
import { formatMoney } from '../owners/formatMoney';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';

function formatDateTime(ts) {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function Row({ label, value, mono }) {
    return (
        <div className="flex justify-between items-start py-2.5 border-b border-gray-100 last:border-0 gap-4">
            <span className="text-xs text-gray-500 shrink-0 w-36">{label}</span>
            <span className={`text-xs text-gray-800 text-right break-all ${mono ? 'font-mono' : 'font-medium'}`}>
                {value || '—'}
            </span>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
            <div className="bg-white rounded-xl border border-gray-100 px-4">
                {children}
            </div>
        </div>
    );
}

const C2BTransactionDrawer = ({ row, onClose, onPosted }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || '';

    const isDriverTarget = row ? row.post_target === 'driver' : false;
    const isCarPosted = row ? row.post_status === 'posted' : false;
    const isDriverPosted = row ? row.driver_post_status === 'posted' : false;
    const needsPost = row ? (isDriverTarget ? !isDriverPosted : !isCarPosted) : false;

    const [postTarget, setPostTarget] = useState((row && row.post_target) || 'car');
    const [plateInput, setPlateInput] = useState((row && row.plate_no) || '');
    const [driverOptions, setDriverOptions] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [payTypeOptions, setPayTypeOptions] = useState([]);
    const [selectedTransType, setSelectedTransType] = useState({ value: 'DPY', label: 'DPY – DEPOSIT PMT' });
    const [posting, setPosting] = useState(false);
    const [postError, setPostError] = useState('');

    const fetchDrivers = useCallback(async () => {
        try {
            const res = await axios.get(`${rootUrl}/drivers`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            const drivers = res.data?.data || res.data || [];
            setDriverOptions(
                drivers.map((d) => ({
                    value: d.id_no,
                    label: `${d.name || ''}${d.id_no ? ` (${d.id_no})` : ''}`.trim(),
                }))
            );
        } catch {
            setDriverOptions([]);
        }
    }, [authToken]);

    const fetchPayTypes = useCallback(async () => {
        try {
            const res = await axios.get(`${rootUrl}/payments/driver-types`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            const types = res.data?.data || res.data || [];
            const opts = types
                .filter(t => t.is_active)
                .map(t => ({ value: t.type, label: `${t.type} – ${t.short_desc}` }));
            setPayTypeOptions(opts);
            const dpy = opts.find(o => o.value === 'DPY');
            if (dpy) setSelectedTransType(dpy);
        } catch {
            setPayTypeOptions([]);
        }
    }, [authToken]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    useEffect(() => {
        if (needsPost) {
            fetchDrivers();
            fetchPayTypes();
        }
    }, [needsPost, fetchDrivers, fetchPayTypes]);

    if (!row) return null;

    const raw = row.raw_payload || {};
    const isMatched = row.match_status === 'matched';
    const payerName = [row.first_name, row.last_name].filter(Boolean).join(' ');

    const handlePost = async () => {
        setPosting(true);
        setPostError('');
        try {
            const body = postTarget === 'driver'
                ? { post_target: 'driver', driver_id: selectedDriver?.value, trans_type: selectedTransType?.value }
                : { post_target: 'car', plate_no: plateInput.toUpperCase() };

            await axios.post(
                `${rootUrl}/api/payments/c2b/${row.id}/post`,
                body,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (onPosted) onPosted();
        } catch (err) {
            setPostError(err.response?.data?.message || 'Failed to post transaction.');
        } finally {
            setPosting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black bg-opacity-30" onClick={onClose} />

            <div className="fixed top-0 right-0 h-full z-50 w-full max-w-md bg-[#faf8f6] shadow-2xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-3">
                        <FaMobileAlt style={{ color: '#FF5733', fontSize: '20px' }} />
                        <div>
                            <p className="font-semibold text-gray-800 font-mono text-sm">{row.trans_id}</p>
                            <p className="text-xs text-gray-500">C2B Transaction</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {row.post_status === 'posted'
                            ? <span className="flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-100 px-2.5 py-1 rounded-full">
                                <FaCheckCircle /> Posted
                              </span>
                            : <span className="flex items-center gap-1.5 text-xs font-medium text-orange-700 bg-orange-100 px-2.5 py-1 rounded-full">
                                <FaExclamationCircle /> Pending
                              </span>
                        }
                        {isMatched
                            ? <span className="flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                                <FaCheckCircle /> Matched
                              </span>
                            : <span className="flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                                <FaExclamationCircle /> Unmatched
                              </span>
                        }
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100" aria-label="Close">
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Amount hero */}
                <div className="px-5 py-5 bg-white border-b border-gray-200 text-center">
                    <p className="text-3xl font-bold text-[#2a2e30]">{formatMoney(row.amount)}</p>
                    <p className="text-sm text-gray-500 mt-1">{formatDateTime(row.trans_time)}</p>
                    {row.week_key && (
                        <p className="text-xs text-gray-400 mt-0.5">Week {row.week_key}</p>
                    )}
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 py-5">

                    {isMatched && row.plate_no && (
                        <Section title="Matched Vehicle">
                            <div className="flex items-center gap-3 py-3">
                                <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                                    <FaCar style={{ color: '#16a34a', fontSize: '16px' }} />
                                </div>
                                <div>
                                    <p className="font-mono font-semibold text-gray-800">{row.plate_no}</p>
                                    <p className="text-xs text-gray-500">Plate matched from bill reference</p>
                                </div>
                            </div>
                        </Section>
                    )}

                    {isMatched && row.post_target === 'driver' && row.driver_id && (
                        <Section title="Matched Driver">
                            <div className="flex items-center gap-3 py-3">
                                <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                                    <FaIdCard style={{ color: '#7c3aed', fontSize: '16px' }} />
                                </div>
                                <div>
                                    <p className="font-mono font-semibold text-gray-800">{row.driver_id}</p>
                                    <p className="text-xs text-gray-500">Driver ID matched from bill reference</p>
                                </div>
                            </div>
                        </Section>
                    )}

                    <Section title="Payment Details">
                        <Row label="Receipt No." value={row.trans_id} mono />
                        <Row label="Shortcode" value={row.shortcode} mono />
                        <Row label="Transaction Type" value={raw.TransactionType} />
                        <Row label="Account Balance" value={raw.OrgAccountBalance ? formatMoney(raw.OrgAccountBalance) : null} />
                        <Row label="Invoice No." value={raw.InvoiceNumber || null} mono />
                        <Row label="Third-Party Trans ID" value={raw.ThirdPartyTransID || null} mono />
                    </Section>

                    <Section title="Payer">
                        <Row label="Name" value={payerName || null} />
                        <Row label="Bill Reference (raw)" value={row.bill_ref_raw} mono />
                        <Row label="Bill Reference (normalised)" value={row.bill_ref_normalized} mono />
                        <Row label="MSISDN (hashed)" value={row.msisdn_hash} mono />
                    </Section>

                    <Section title="Posting Status">
                        <Row label="Post Target" value={row.post_target === 'driver' ? 'Driver' : 'Car'} />
                        <Row label="Car Post Status" value={row.post_status === 'posted' ? 'Posted' : 'Pending'} />
                        <Row label="Car Transaction ID" value={row.posted_trans_id || null} mono />
                        <Row label="Car Posted At" value={formatDateTime(row.posted_at)} />
                        <Row label="Driver Post Status" value={row.driver_post_status === 'posted' ? 'Posted' : 'Pending'} />
                        <Row label="Driver Transaction ID" value={row.driver_posted_trans_id || null} mono />
                        <Row label="Driver Posted At" value={formatDateTime(row.driver_posted_at)} />
                        <Row label="Cashbook Status" value={row.cb_posted === 'posted' ? 'Posted' : 'Pending'} />
                        <Row label="Cashbook Entry ID" value={row.cashbook_entry_id ? String(row.cashbook_entry_id) : null} mono />
                    </Section>

                    <Section title="System">
                        <Row label="Week Key" value={row.week_key} mono />
                        <Row label="Received" value={formatDateTime(row.input_date)} />
                        <Row label="Record ID" value={String(row.id)} mono />
                    </Section>

                    {needsPost && (
                        <Section title="Post Transaction">
                            <div className="py-3 space-y-3">
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { setPostTarget('car'); setPostError(''); }}
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${postTarget === 'car' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400'}`}
                                    >
                                        Car
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setPostTarget('driver'); setPostError(''); }}
                                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${postTarget === 'driver' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'}`}
                                    >
                                        Driver
                                    </button>
                                </div>

                                {postTarget === 'car' ? (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Plate Number</label>
                                        <input
                                            type="text"
                                            value={plateInput}
                                            onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                                            placeholder="e.g. KAA 123A"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:border-orange-400 uppercase"
                                        />
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Driver (search by name or ID)</label>
                                            <Select
                                                value={selectedDriver}
                                                onChange={setSelectedDriver}
                                                options={driverOptions}
                                                placeholder="Select driver…"
                                                isClearable
                                                isSearchable
                                                styles={{
                                                    control: (base, state) => ({
                                                        ...base,
                                                        minHeight: '34px',
                                                        borderColor: state.isFocused ? '#7c3aed' : '#d1d5db',
                                                        boxShadow: 'none',
                                                        fontSize: '12px',
                                                        '&:hover': { borderColor: '#7c3aed' },
                                                    }),
                                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Transaction Type</label>
                                            <Select
                                                value={selectedTransType}
                                                onChange={setSelectedTransType}
                                                options={payTypeOptions}
                                                placeholder="Select type…"
                                                isSearchable
                                                styles={{
                                                    control: (base, state) => ({
                                                        ...base,
                                                        minHeight: '34px',
                                                        borderColor: state.isFocused ? '#7c3aed' : '#d1d5db',
                                                        boxShadow: 'none',
                                                        fontSize: '12px',
                                                        '&:hover': { borderColor: '#7c3aed' },
                                                    }),
                                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {postError && (
                                    <p className="text-xs text-red-600">{postError}</p>
                                )}

                                <button
                                    type="button"
                                    onClick={handlePost}
                                    disabled={posting || (postTarget === 'car' ? !plateInput : (!selectedDriver || !selectedTransType))}
                                    className="w-full py-2 rounded-lg text-xs font-semibold bg-[#2a2e30] text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {posting ? 'Posting…' : 'Post Transaction'}
                                </button>
                            </div>
                        </Section>
                    )}

                </div>
            </div>
        </>
    );
};

export default C2BTransactionDrawer;
